import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import styled from 'styled-components';

import Loading from 'components/Loading';
import MainContainer from 'components/MainContainer';
import { safeReturnPath } from 'utils/returnPath';
import useUnifiedLoginKickoff from 'utils/useUnifiedLoginKickoff';
import WorkOSEmailForm from 'components/Reports/CandidateLogin/WorkOS/WorkOSEmailForm';
import WorkOSCodeForm from 'components/Reports/CandidateLogin/WorkOS/WorkOSCodeForm';
import WorkOSDobForm from 'components/Reports/CandidateLogin/WorkOS/WorkOSDobForm';
import WorkOSGoogleButton from 'components/Reports/CandidateLogin/WorkOS/WorkOSGoogleButton';
import {
  sendWorkOSMagicCode,
  verifyWorkOSMagicCode,
  verifyWorkOSDob,
  initiateWorkOSGoogleAuth,
  getWorkosDobAuthMethod,
  isWorkosDobPending,
  workosReset,
  workosResetUi,
  workosDobRequired,
} from 'actions/workosLogin';
import { trackAnalyticsEvent } from 'actions/analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from 'lib/analytics';
import useAuthGateState from 'hooks/useAuthGateState';
import { isSessionStorageSupported } from 'lib/helpers';
import {
  CHECKR_APPLICANTS_EU_BASE_URL,
  PATHS,
  SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY,
} from '../../constants';

const WelcomeSection = styled.div`
  margin-bottom: 1.5rem;

  h2 {
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 16px;
    line-height: 24px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: var(--cds-text-secondary, #525252);
  font-size: 12px;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--cds-border-subtle, #e0e0e0);
  }

  span {
    padding: 0 1rem;
  }
`;

const LinksSection = styled.div`
  margin-top: 2rem;
  font-size: 14px;
  line-height: 1.5;

  > a,
  > .ssn-fallback,
  > .international-link {
    display: block;
    margin-top: 0.75rem;
  }
`;

const FormCard = styled.div`
  max-width: 480px;
  margin: 0 auto;

  .cds--inline-notification {
    max-width: 100%;
    margin: 1rem 0;
  }
`;

const WorkOSLogin: React.FC = () => {
  const dispatch = useDispatch();
  const { state: locationState } = useLocation();
  const {
    candidateLogin: {
      workosStep,
      workosError,
      candidateEmail,
      processing,
      loggedIn,
    },
  } = useAuthGateState();
  const fromOAuthCallback = locationState?.fromOAuthCallback === true;
  const hasInitialized = useRef(false);
  const { holdsLogin: unifiedKickoffHoldsLogin } = useUnifiedLoginKickoff();

  useEffect(() => {
    // Nothing here belongs to a visit that is on its way to the broker: the
    // form is not rendering, and recording a page view for it would count a
    // screen the candidate never saw against Personal conversion. If kickoff
    // stands down, this runs then.
    if (unifiedKickoffHoldsLogin) return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (isWorkosDobPending() && !workosError) {
      dispatch(workosDobRequired({ preserveAnalytics: true }));
    } else if (!fromOAuthCallback) {
      // UI-only reset — do not clear persisted JWT/session keys. Clearing here
      // races Layout's checkForActiveAuthSession (child effects run first) and
      // logs out an already-authenticated user who opens /login/email.
      dispatch(workosResetUi());
    }
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_PAGE_VIEWED,
        {},
      ),
    );
  }, [dispatch, fromOAuthCallback, workosError, unifiedKickoffHoldsLogin]);

  const prevStepRef = useRef(workosStep);
  // Track once per mount so OAuth landings (already on 'dob') still fire, while
  // email→dob transitions and remounts don't double-count within the same visit.
  const hasTrackedDobPageView = useRef(false);
  useEffect(() => {
    if (prevStepRef.current !== 'code' && workosStep === 'code') {
      dispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_CODE_PAGE_VIEWED,
          {},
        ),
      );
    }
    if (workosStep === 'dob' && !hasTrackedDobPageView.current) {
      hasTrackedDobPageView.current = true;
      dispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_PAGE_VIEWED,
          {
            [ANALYTICS_PROPERTIES.AUTH_METHOD]: getWorkosDobAuthMethod(),
          },
        ),
      );
    }
    prevStepRef.current = workosStep;
  }, [workosStep, dispatch]);

  useEffect(() => {
    if (loggedIn && isSessionStorageSupported()) {
      window.sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY);
    }
  }, [loggedIn]);

  if (loggedIn) {
    const storedPrevLocation = isSessionStorageSupported()
      ? window.sessionStorage.getItem(SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY)
      : null;
    const loggedInTarget =
      safeReturnPath(locationState?.prevLocation ?? storedPrevLocation) ||
      PATHS.REPORTS_OVERVIEW;
    return <Navigate to={loggedInTarget} />;
  }
  if (unifiedKickoffHoldsLogin) {
    return (
      <MainContainer data-testid='workos-login-page'>
        <Loading />
      </MainContainer>
    );
  }

  const handleEmailSubmit = (email: string) => {
    dispatch(sendWorkOSMagicCode(email));
  };

  const handleSuggestionShown = (
    originalDomain: string,
    suggestedDomain: string,
  ) => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_SHOWN,
        { originalDomain, suggestedDomain },
      ),
    );
  };

  const handleSuggestionAccepted = (
    originalDomain: string,
    suggestedDomain: string,
  ) => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_ACCEPTED,
        { originalDomain, suggestedDomain },
      ),
    );
  };

  const handleSuggestionDismissed = (
    originalDomain: string,
    suggestedDomain: string,
  ) => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_DISMISSED,
        { originalDomain, suggestedDomain },
      ),
    );
  };

  const handleCodeSubmit = (code: string) => {
    dispatch(verifyWorkOSMagicCode(candidateEmail, code));
  };

  const handleDobSubmit = (dob: string) => {
    dispatch(verifyWorkOSDob(dob));
  };

  const handleResendCode = () => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_RESEND_CODE_CLICKED,
        {},
      ),
    );
    dispatch(sendWorkOSMagicCode(candidateEmail));
  };

  const handleBackToEmail = (sourceStep: 'code' | 'dob') => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_BACK_TO_EMAIL_CLICKED,
        { source_step: sourceStep },
      ),
    );
    dispatch(workosReset());
  };

  const handleGoogleAuth = () => {
    if (locationState?.prevLocation) {
      window.sessionStorage.setItem(
        SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY,
        locationState.prevLocation,
      );
    }
    dispatch(initiateWorkOSGoogleAuth());
  };

  return (
    <MainContainer data-testid='workos-login-page'>
      <FormCard>
        {workosStep === 'email' && (
          <>
            <WelcomeSection>
              <h2>{i18n.getStr('components.WorkOSLogin.welcome')}</h2>
              <p>{i18n.getStr('components.WorkOSLogin.subtitle')}</p>
            </WelcomeSection>
            {workosError && (
              <M.InlineNotification
                kind='error'
                title={i18n.getStr(
                  workosError === 'oauthError'
                    ? 'components.WorkOSLogin.oauthError'
                    : 'components.WorkOSLogin.genericError',
                )}
                hideCloseButton
                data-testid='workos-email-step-error'
              />
            )}
            <WorkOSEmailForm
              onSubmit={handleEmailSubmit}
              onSuggestionShown={handleSuggestionShown}
              onSuggestionAccepted={handleSuggestionAccepted}
              onSuggestionDismissed={handleSuggestionDismissed}
              processing={processing}
            />
            <Divider>
              <span>{i18n.getStr('components.WorkOSLogin.orDivider')}</span>
            </Divider>
            <WorkOSGoogleButton
              onClick={handleGoogleAuth}
              disabled={processing}
            />
          </>
        )}
        {workosStep === 'code' && (
          <WorkOSCodeForm
            email={candidateEmail}
            errorMessage={workosError}
            onSubmit={handleCodeSubmit}
            onResend={handleResendCode}
            onBackToEmail={() => handleBackToEmail('code')}
            processing={processing}
          />
        )}
        {workosStep === 'dob' && (
          <WorkOSDobForm
            errorMessage={workosError}
            onSubmit={handleDobSubmit}
            onStartOver={() => handleBackToEmail('dob')}
            processing={processing}
          />
        )}

        <LinksSection>
          <span
            className='international-link'
            data-testid='workos-international-text'
          >
            {i18n.getStr('components.WorkOSLogin.internationalPrefix')}
            <M.Link
              href={`${CHECKR_APPLICANTS_EU_BASE_URL}/international`}
              data-testid='workos-international-link'
            >
              {i18n.getStr('components.WorkOSLogin.internationalClickHere')}
            </M.Link>
          </span>
          <span className='ssn-fallback' data-testid='workos-ssn-fallback'>
            {i18n.getStr('components.WorkOSLogin.ssnFallbackPrefix')}
            <Link
              to='/login?force=ssn'
              state={locationState}
              data-testid='workos-ssn-fallback-link'
              onClick={() => {
                dispatch(workosReset());
                dispatch(
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_SSN_FALLBACK_CLICKED,
                    {},
                  ),
                );
              }}
            >
              {i18n.getStr('components.WorkOSLogin.ssnFallbackClickHere')}
            </Link>
            {i18n.getStr('components.WorkOSLogin.ssnFallbackSuffix')}
          </span>
        </LinksSection>
      </FormCard>
    </MainContainer>
  );
};

export default i18n.renderTranslation()(WorkOSLogin);
